import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { getMe, updateName, changePassword, bindPlatform, unbindPlatform, getWecomConfig } from '@/api/auth';
import type { User } from '@/types/user';

type MessageType = { type: 'success' | 'error'; text: string } | null;

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Local user state, seeded from AuthContext and kept in sync with API responses
  const [user, setUser] = useState<User | null>(authUser);

  useEffect(() => {
    setUser(authUser);
  }, [authUser]);

  // Fallback: if AuthContext hasn't populated the user yet, fetch it directly
  useEffect(() => {
    if (!authUser) {
      getMe()
        .then((u) => setUser(u))
        .catch(() => {
          // ignore — user stays null, page renders defaults
        });
    }
  }, [authUser]);

  // Handle redirect back from WeCom bind OAuth flow (?wecom=bound)
  useEffect(() => {
    if (searchParams.get('wecom') === 'bound') {
      getMe()
        .then((u) => {
          setUser(u);
          setBindMessage({ type: 'success', text: '企业微信绑定成功' });
          setTimeout(() => setBindMessage(null), 3000);
        })
        .catch(() => {
          // ignore
        })
        .finally(() => {
          const next = new URLSearchParams(searchParams);
          next.delete('wecom');
          setSearchParams(next, { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName = user?.name ?? '';
  const email = user?.email ?? '';
  const role = user?.role ?? '';
  const joinDate = user?.joinDate ?? '';

  // Edit name
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState(displayName);
  const [nameMessage, setNameMessage] = useState<MessageType>(null);
  const [savingName, setSavingName] = useState(false);

  // Change password
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordMessage, setPasswordMessage] = useState<MessageType>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Account binding
  const dingtalkBound = user?.dingtalkBound ?? false;
  const dingtalkAccountValue = user?.dingtalkAccount ?? '';
  const [dingtalkAccount, setDingtalkAccount] = useState('');
  const [showDingtalkModal, setShowDingtalkModal] = useState(false);
  const [bindMessage, setBindMessage] = useState<MessageType>(null);
  const [binding, setBinding] = useState(false);

  const feishuBound = user?.feishuBound ?? false;
  const feishuAccountValue = user?.feishuAccount ?? '';
  const [feishuAccount, setFeishuAccount] = useState('');
  const [showFeishuModal, setShowFeishuModal] = useState(false);

  const wecomBound = user?.wecomBound ?? false;

  // Unbind confirmation
  const [unbindConfirm, setUnbindConfirm] = useState<{ type: 'dingtalk' | 'feishu' | 'wecom' } | null>(null);
  const [unbinding, setUnbinding] = useState(false);

  const handleSaveName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed) {
      setNameMessage({ type: 'error', text: '姓名不能为空' });
      return;
    }
    if (trimmed === displayName) {
      setIsEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const updated = await updateName(trimmed);
      setUser(updated);
      setIsEditingName(false);
      setNameMessage({ type: 'success', text: `姓名已更新为"${trimmed}"` });
      setTimeout(() => setNameMessage(null), 2500);
    } catch {
      setNameMessage({ type: 'error', text: '姓名修改失败，请稍后重试' });
    } finally {
      setSavingName(false);
    }
  };

  const handleCancelEditName = () => {
    setEditNameValue(displayName);
    setIsEditingName(false);
    setNameMessage(null);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!passwordForm.oldPassword) {
      setPasswordMessage({ type: 'error', text: '请输入当前密码' });
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordMessage({ type: 'error', text: '请输入新密码' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: '新密码长度不能少于6位' });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: '两次输入的新密码不一致' });
      return;
    }
    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordMessage({ type: 'error', text: '新密码不能与当前密码相同' });
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      setPasswordMessage({ type: 'success', text: '密码修改成功，下次登录请使用新密码' });
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswords({ old: false, new: false, confirm: false });
      setTimeout(() => setPasswordMessage(null), 3500);
    } catch {
      setPasswordMessage({ type: 'error', text: '密码修改失败，请确认当前密码是否正确' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleBindDingtalk = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = dingtalkAccount.trim();
    if (!trimmed) {
      setBindMessage({ type: 'error', text: '请输入钉钉账号（手机号或邮箱）' });
      return;
    }
    setBinding(true);
    try {
      const updated = await bindPlatform('dingtalk', trimmed);
      setUser(updated);
      setShowDingtalkModal(false);
      setDingtalkAccount('');
      setBindMessage({ type: 'success', text: `钉钉账号"${trimmed}"绑定成功` });
      setTimeout(() => setBindMessage(null), 3000);
    } catch {
      setBindMessage({ type: 'error', text: '钉钉账号绑定失败，请稍后重试' });
    } finally {
      setBinding(false);
    }
  };

  const handleBindFeishu = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = feishuAccount.trim();
    if (!trimmed) {
      setBindMessage({ type: 'error', text: '请输入飞书账号（手机号或邮箱）' });
      return;
    }
    setBinding(true);
    try {
      const updated = await bindPlatform('feishu', trimmed);
      setUser(updated);
      setShowFeishuModal(false);
      setFeishuAccount('');
      setBindMessage({ type: 'success', text: `飞书账号"${trimmed}"绑定成功` });
      setTimeout(() => setBindMessage(null), 3000);
    } catch {
      setBindMessage({ type: 'error', text: '飞书账号绑定失败，请稍后重试' });
    } finally {
      setBinding(false);
    }
  };

  const handleUnbind = async () => {
    if (!unbindConfirm) return;
    setUnbinding(true);
    try {
      const updated = await unbindPlatform(unbindConfirm.type);
      setUser(updated);
      const unbindText: Record<'dingtalk' | 'feishu' | 'wecom', string> = {
        dingtalk: '钉钉账号已解除绑定',
        feishu: '飞书账号已解除绑定',
        wecom: '企业微信账号已解除绑定',
      };
      setBindMessage({
        type: 'success',
        text: unbindText[unbindConfirm.type],
      });
      setTimeout(() => setBindMessage(null), 3000);
    } catch {
      setBindMessage({ type: 'error', text: '解除绑定失败，请稍后重试' });
    } finally {
      setUnbinding(false);
      setUnbindConfirm(null);
    }
  };

  const handleWecomBind = async () => {
    try {
      const cfg = await getWecomConfig();
      if (!cfg.enabled) return;
      const state = Math.random().toString(36).slice(2);
      sessionStorage.setItem('wecom_state', state);
      sessionStorage.setItem('wecom_intent', 'bind');
      const url =
        `https://login.work.weixin.qq.com/wwlogin/sso/login?login_type=CorpApp` +
        `&appid=${encodeURIComponent(cfg.corpId)}` +
        `&agentid=${encodeURIComponent(cfg.agentId)}` +
        `&redirect_uri=${encodeURIComponent(cfg.redirectUri)}` +
        `&state=${state}`;
      window.location.href = url;
    } catch {
      // ignore
    }
  };

  const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5 max-w-[960px] mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">用户信息管理</h1>
            <p className="text-sm text-foreground-500 mt-1">管理个人账户信息、密码和第三方平台绑定</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-2.5 border border-background-200 text-foreground-600 hover:text-foreground-800 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-base"></i>
              返回首页
            </Link>
          </div>
        </div>

        {/* Global message */}
        {bindMessage && (
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium animate-in ${
              bindMessage.type === 'success'
                ? 'bg-accent-50 text-accent-700 border border-accent-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            <i className={bindMessage.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            {bindMessage.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column - User profile card */}
          <div className="lg:col-span-1">
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
              <div className="flex flex-col items-center text-center">
                {/* Avatar */}
                <div className="w-20 h-20 rounded-full bg-secondary-200 flex items-center justify-center mb-4">
                  <span className="text-2xl font-heading font-semibold text-secondary-700">
                    {displayName.charAt(0)}
                  </span>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900">{displayName}</h2>
                <span className="inline-flex px-2.5 py-1 mt-1.5 bg-primary-50 text-primary-700 rounded-md text-xs font-medium">
                  {role}
                </span>
                <div className="w-full mt-5 pt-5 border-t border-background-200/70 space-y-3">
                  <div className="flex items-center gap-2.5 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-foreground-500 text-sm"></i>
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm text-foreground-800 truncate">{email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-line text-foreground-500 text-sm"></i>
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-xs text-foreground-500">注册日期</p>
                      <p className="text-sm text-foreground-800">{joinDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-user-line text-foreground-500 text-sm"></i>
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-xs text-foreground-500">账户状态</p>
                      <span className="inline-flex items-center gap-1 text-sm text-accent-600 font-medium">
                        <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                        正常
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 mt-4">
              <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-3">快捷操作</h3>
              <div className="space-y-1.5">
                <Link
                  to="/"
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-building-2-line text-base"></i>
                  企业列表
                </Link>
                <Link
                  to="/tags"
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-price-tag-3-line text-base"></i>
                  标签管理
                </Link>
                <Link
                  to="/import"
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-file-upload-line text-base"></i>
                  数据导入
                </Link>
              </div>
            </div>
          </div>

          {/* Right column - Settings */}
          <div className="lg:col-span-2 space-y-5">
            {/* Edit name */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 md:px-6 py-4 border-b border-background-200/70">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-800">修改姓名</h3>
                    <p className="text-xs text-foreground-500">更新您在系统中显示的姓名</p>
                  </div>
                </div>
              </div>
              <div className="p-5 md:p-6">
                {nameMessage && (
                  <div
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm mb-4 ${
                      nameMessage.type === 'success'
                        ? 'bg-accent-50 text-accent-700 border border-accent-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    <i className={nameMessage.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
                    {nameMessage.text}
                  </div>
                )}
                {isEditingName ? (
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-sm">
                      <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                      <input
                        type="text"
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName();
                          if (e.key === 'Escape') handleCancelEditName();
                        }}
                        autoFocus
                        placeholder="请输入姓名"
                        className="w-full h-10 pl-9 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <button
                      onClick={handleSaveName}
                      disabled={savingName}
                      className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {savingName ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={handleCancelEditName}
                      className="px-4 py-2.5 border border-background-200 text-foreground-600 hover:text-foreground-800 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      取消
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-foreground-800 font-medium">{displayName}</span>
                      <span className="text-xs text-foreground-400">当前显示姓名</span>
                    </div>
                    <button
                      onClick={() => {
                        setEditNameValue(displayName);
                        setIsEditingName(true);
                        setNameMessage(null);
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 border border-background-200 text-foreground-600 hover:text-foreground-800 hover:bg-background-100 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line text-sm"></i>
                      编辑
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Change password */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 md:px-6 py-4 border-b border-background-200/70">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-800">修改密码</h3>
                    <p className="text-xs text-foreground-500">修改后需使用新密码登录</p>
                  </div>
                </div>
              </div>
              <div className="p-5 md:p-6">
                {passwordMessage && (
                  <div
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm mb-4 ${
                      passwordMessage.type === 'success'
                        ? 'bg-accent-50 text-accent-700 border border-accent-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    <i className={passwordMessage.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
                    {passwordMessage.text}
                  </div>
                )}
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">当前密码</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                      <input
                        type={showPasswords.old ? 'text' : 'password'}
                        value={passwordForm.oldPassword}
                        onChange={(e) => setPasswordForm((prev) => ({ ...prev, oldPassword: e.target.value }))}
                        placeholder="请输入当前密码"
                        className="w-full h-10 pl-9 pr-10 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('old')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                      >
                        <i className={`${showPasswords.old ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                    </div>
                  </div>
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">新密码</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="至少6位新密码"
                        className="w-full h-10 pl-9 pr-10 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                      >
                        <i className={`${showPasswords.new ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                    </div>
                  </div>
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">确认新密码</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="请再次输入新密码"
                        className="w-full h-10 pl-9 pr-10 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                      >
                        <i className={`${showPasswords.confirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="flex items-center gap-2 px-5 py-2.5 bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {changingPassword ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                        修改中...
                      </>
                    ) : (
                      <>
                        <i className="ri-key-2-line text-sm"></i>
                        修改密码
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Third-party account binding */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 md:px-6 py-4 border-b border-background-200/70">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-link text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-800">第三方平台绑定</h3>
                    <p className="text-xs text-foreground-500">绑定钉钉、飞书或企业微信账号，方便接收通知和协作</p>
                  </div>
                </div>
              </div>

              {/* DingTalk */}
              <div className="px-5 md:px-6 py-4 border-b border-background-200/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0089FF]/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-dingding-line text-xl text-[#0089FF]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">钉钉</p>
                      {dingtalkBound ? (
                        <p className="text-xs text-foreground-500">{dingtalkAccountValue}</p>
                      ) : (
                        <p className="text-xs text-foreground-400">未绑定</p>
                      )}
                    </div>
                  </div>
                  {dingtalkBound ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                        已绑定
                      </span>
                      <button
                        onClick={() => setUnbindConfirm({ type: 'dingtalk' })}
                        className="px-3 py-1.5 text-xs text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        解绑
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowDingtalkModal(true)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#0089FF] hover:bg-[#0078E8] text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-sm"></i>
                      绑定钉钉
                    </button>
                  )}
                </div>
              </div>

              {/* Feishu */}
              <div className="px-5 md:px-6 py-4 border-b border-background-200/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#3370FF]/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-rocket-line text-xl text-[#3370FF]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">飞书</p>
                      {feishuBound ? (
                        <p className="text-xs text-foreground-500">{feishuAccountValue}</p>
                      ) : (
                        <p className="text-xs text-foreground-400">未绑定</p>
                      )}
                    </div>
                  </div>
                  {feishuBound ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                        已绑定
                      </span>
                      <button
                        onClick={() => setUnbindConfirm({ type: 'feishu' })}
                        className="px-3 py-1.5 text-xs text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        解绑
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowFeishuModal(true)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#3370FF] hover:bg-[#2B5FE0] text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-sm"></i>
                      绑定飞书
                    </button>
                  )}
                </div>
              </div>

              {/* WeCom */}
              <div className="px-5 md:px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#07C160]/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-xl text-[#07C160]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">企业微信</p>
                      {wecomBound ? (
                        <p className="text-xs text-foreground-500">已通过企业微信扫码绑定</p>
                      ) : (
                        <p className="text-xs text-foreground-400">未绑定</p>
                      )}
                    </div>
                  </div>
                  {wecomBound ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                        已绑定
                      </span>
                      <button
                        onClick={() => setUnbindConfirm({ type: 'wecom' })}
                        className="px-3 py-1.5 text-xs text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        解绑
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleWecomBind}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#07C160] hover:bg-[#06AD56] text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-sm"></i>
                      绑定企业微信
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DingTalk bind modal */}
      {showDingtalkModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setShowDingtalkModal(false)}
        >
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-background-200/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0089FF]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-dingding-line text-xl text-[#0089FF]"></i>
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900">绑定钉钉账号</h3>
                  <p className="text-xs text-foreground-500">输入钉钉账号完成绑定</p>
                </div>
              </div>
              <button
                onClick={() => setShowDingtalkModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <form onSubmit={handleBindDingtalk}>
              <div className="p-6">
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">钉钉账号</label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    value={dingtalkAccount}
                    onChange={(e) => setDingtalkAccount(e.target.value)}
                    placeholder="请输入钉钉绑定的手机号或邮箱"
                    autoFocus
                    className="w-full h-11 pl-9 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-[#0089FF] focus:ring-2 focus:ring-[#0089FF]/20 transition-all"
                  />
                </div>
                <p className="text-xs text-foreground-400 mt-2 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  绑定后将可通过钉钉接收系统通知
                </p>
              </div>
              <div className="border-t border-background-200/70 px-6 py-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDingtalkModal(false)}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={binding}
                  className="px-4 py-2.5 bg-[#0089FF] hover:bg-[#0078E8] disabled:bg-[#0089FF]/60 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {binding ? '绑定中...' : '确认绑定'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feishu bind modal */}
      {showFeishuModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setShowFeishuModal(false)}
        >
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-background-200/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#3370FF]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-rocket-line text-xl text-[#3370FF]"></i>
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900">绑定飞书账号</h3>
                  <p className="text-xs text-foreground-500">输入飞书账号完成绑定</p>
                </div>
              </div>
              <button
                onClick={() => setShowFeishuModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <form onSubmit={handleBindFeishu}>
              <div className="p-6">
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">飞书账号</label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    value={feishuAccount}
                    onChange={(e) => setFeishuAccount(e.target.value)}
                    placeholder="请输入飞书绑定的手机号或邮箱"
                    autoFocus
                    className="w-full h-11 pl-9 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-[#3370FF] focus:ring-2 focus:ring-[#3370FF]/20 transition-all"
                  />
                </div>
                <p className="text-xs text-foreground-400 mt-2 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  绑定后将可通过飞书接收系统通知和协作消息
                </p>
              </div>
              <div className="border-t border-background-200/70 px-6 py-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowFeishuModal(false)}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={binding}
                  className="px-4 py-2.5 bg-[#3370FF] hover:bg-[#2B5FE0] disabled:bg-[#3370FF]/60 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {binding ? '绑定中...' : '确认绑定'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unbind confirm modal */}
      {unbindConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setUnbindConfirm(null)}
        >
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-link-unlink text-lg text-red-500"></i>
                </div>
                <div>
                  <h3 className="text-base font-heading font-bold text-foreground-900">确认解除绑定</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">解除后相关通知将不再发送到该平台</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-4">
                <p className="text-sm text-foreground-700">
                  确定要解除
                  {unbindConfirm.type === 'dingtalk' ? '钉钉' : unbindConfirm.type === 'feishu' ? '飞书' : '企业微信'}
                  账号的绑定吗？
                </p>
                <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  解绑后仍可随时重新绑定
                </p>
              </div>
            </div>
            <div className="border-t border-background-200/70 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setUnbindConfirm(null)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleUnbind}
                disabled={unbinding}
                className="px-4 py-2.5 bg-red-500 hover:bg-red-600 disabled:bg-red-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {unbinding ? '解绑中...' : '确认解绑'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
